import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { allCategoryGroupNames } from 'config/filters'
import { RootState } from 'reducers/store'
import { CategoryGroupName, Filter } from 'typings/filters'
import { Configuration, FiltersControllerApiFactory, Filters } from '@ecosystem/preference-nest-api'
import { selectAppliedSavedFilters } from 'reducers/filtering/selectors'
import { formatSavedFilters } from 'util/migration'
import { loadFromLocalStorage, saveToLocalStorage } from 'util/localStorage'

export type FilterSet = {
  id?: string
  name: string
  filters: Record<CategoryGroupName, Filter[]>
  isApplied?: boolean
  isDisabled?: boolean
}
export type SavedFilters = FilterSet[]
export type PartialFilter = Partial<Filter> & Pick<Filter, 'variable_id' | 'answer_code'>

const initialState: SavedFilters = []

const getPrefService = (accessToken: string) => {
  const config = new Configuration({
    accessToken,
    basePath: `${window.origin}/preference-filters`,
  })
  return FiltersControllerApiFactory(config)
}

export const createFilter = async (prefService: any, oldFilter: any) => {
  try {
    await prefService.filtersControllerCreate({
      context: { product: 'CIE' },
      name: oldFilter.name,
      data: oldFilter,
    })
    return { success: true, filter: oldFilter }
  } catch (err: any) {
    if (err?.response?.status !== 422 || err?.response?.data?.message !== 'Name already exists') {
      return { success: false, filter: oldFilter }
    }
    return { success: true, filter: null }
  }
}

export const updateLocalStorageOnMigration = (
  storageKey: string | undefined,
  failed: any[],
  migratedFilters: any[]
) => {
  if (failed.length === 0) {
    localStorage.removeItem(`${storageKey}_filters.saved`)
  } else {
    saveToLocalStorage({ storageKey, key: 'filters.saved', data: failed })
  }

  if (migratedFilters.length > 0) {
    saveToLocalStorage({ storageKey, key: 'filters.migrated', data: migratedFilters })
  }
}

export const migrateFilters = async (state: RootState) => {
  const storageKey = state.user.hash
  const savedFilters = loadFromLocalStorage({ storageKey, key: 'filters.saved' })
  const migratedFilters = loadFromLocalStorage({ storageKey, key: 'filters.migrated' }) || []
  const failed = []
  const token = state.user.jwt
  const prefService = getPrefService(token as string)

  if (savedFilters?.length > 0) {
    for (const oldFilter of savedFilters) {
      const result = await createFilter(prefService, oldFilter)
      if (result.success) {
        if (result.filter) migratedFilters.push(result.filter)
      } else {
        failed.push(result.filter)
      }
    }
    updateLocalStorageOnMigration(storageKey, failed, migratedFilters)
  }
}

export const saveFilters = createAsyncThunk(
  'saved/saveFilters',
  async (payload: FilterSet, { getState }) => {
    const token = (getState() as RootState).user.jwt
    const prefService = getPrefService(token as string)
    const {
      data: { id },
    } = await prefService.filtersControllerCreate({
      context: {
        product: 'CIE',
      },
      name: payload.name,
      data: payload,
    })
    const d: FilterSet = {
      ...payload,
      id,
    }
    return d
  }
)

export const applySavedFilter = createAsyncThunk(
  'saved/applySavedFilter',
  async (payload: string, { getState }) => {
    const state = getState() as RootState
    const token = state.user.jwt
    const prefService = getPrefService(token!)

    const toApply = state.filters.saved.find((sf: FilterSet) => sf.name === payload)
    if (!toApply || toApply?.isApplied === true) return

    const currentlyApplied: FilterSet | undefined = selectAppliedSavedFilters(state)

    if (currentlyApplied?.id) {
      await prefService.filtersControllerUpdateById(currentlyApplied.id, {
        data: { ...currentlyApplied, isApplied: false },
      })
    }

    if (toApply.id) {
      await prefService.filtersControllerUpdateById(toApply?.id, {
        data: { ...toApply, isApplied: true },
      })
    }
    return payload
  }
)

export const unapplySavedFilters = createAsyncThunk(
  'saved/unapplySavedFilters',
  async (_payload: undefined, { getState }) => {
    const state = getState() as RootState
    const token = state.user.jwt
    const prefService = getPrefService(token!)
    const currentlyApplied: FilterSet | undefined = selectAppliedSavedFilters(state)

    if (currentlyApplied?.id) {
      prefService.filtersControllerUpdateById(currentlyApplied.id, {
        data: { ...currentlyApplied, isApplied: false },
      })
    }
  }
)
export const disableSavedFilters = createAsyncThunk(
  'saved/disableSavedFilters',
  async (payload: string, { getState }) => {
    const state = getState() as RootState

    const token = state.user.jwt
    const prefService = getPrefService(token!)

    const toDisable = state.filters.saved.find((sf: FilterSet) => sf.name === payload)
    if (!toDisable || toDisable?.isDisabled === true) return

    if (toDisable.id) {
      await prefService.filtersControllerUpdateById(toDisable?.id, {
        data: { ...toDisable, isDisabled: true },
      })
    }

    return payload
  }
)

export const removeSavedFilter = createAsyncThunk(
  'saved/removeSavedFilter',
  async (payload: { id: string | undefined; name: string }, { getState }) => {
    if (!payload.id) throw new Error('id not defined')
    const state = getState() as RootState
    const token = state.user.jwt
    const prefService = getPrefService(token!)
    await prefService.filtersControllerDeleteById(payload.id)
    return payload
  }
)

export const savedFiltersToFilterSet = (result: Filters[]): FilterSet[] => {
  const sanitisedResponse = (result || []).map(
    ({
      id,
      data: { name, filters, isApplied, isDisabled },
    }: {
      id: string
      name: string
      data: any
    }) => {
      return {
        id,
        name,
        filters,
        isApplied,
        isDisabled,
      }
    }
  )

  return formatSavedFilters(sanitisedResponse)
}

export const checkApplied = async (
  result: Filters[],
  prefService: ReturnType<typeof getPrefService>
) => {
  const applied = result.filter((res) => {
    return (res.data as FilterSet).isApplied
  })
  if (applied.length > 1) {
    for (const filterSet of applied) {
      await prefService.filtersControllerUpdateById(filterSet.id, {
        data: {
          ...filterSet.data,
          isApplied: false,
          isDisabled: false,
        },
      })
    }

    const refetchedFilters = await prefService.filtersControllerFindAll('CIE')
    return refetchedFilters.data
  }
  return result
}

export const getPreferenceFilters = createAsyncThunk(
  'saved/getPreferenceFilters',
  async (_payload: undefined, { getState }) => {
    const state = getState() as RootState
    // Migrate from local storage to preference filters API
    // START
    migrateFilters(state)
    // END
    const token = state.user.jwt
    const prefService = getPrefService(token!)
    const result = await prefService.filtersControllerFindAll('CIE')
    const resultData = await checkApplied(result.data, prefService)
    return savedFiltersToFilterSet(resultData)
  }
)

export const IsFilterType = (categoryGroupName: string) => (itemToCheck: any) =>
  typeof itemToCheck === 'object' &&
  typeof itemToCheck.answer_code === 'number' &&
  typeof itemToCheck.variable_id === 'number' &&
  itemToCheck.categoryGroup === categoryGroupName &&
  typeof itemToCheck.desc === 'string' &&
  typeof itemToCheck.label === 'string'

export const isSavedFiltersType = (itemToCheck: any) =>
  Array.isArray(itemToCheck) &&
  itemToCheck.every(
    (arrayItem) =>
      typeof arrayItem === 'object' &&
      typeof arrayItem.name === 'string' &&
      arrayItem.name.length > 0 &&
      typeof arrayItem.filters === 'object' &&
      allCategoryGroupNames.every((name) => {
        const categoryGroup = arrayItem.filters[name]

        return Array.isArray(categoryGroup) && categoryGroup.every(IsFilterType(name))
      })
  )

const savedFiltersSlice = createSlice({
  name: 'saved',
  initialState,
  reducers: {
    saveImportedFilters(state, action: PayloadAction<SavedFilters>) {
      if (isSavedFiltersType(action.payload)) {
        return action.payload
      }
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveFilters.fulfilled, (state, action) => {
      return [...state, action.payload]
    })
    builder.addCase(applySavedFilter.fulfilled, (state, action) => {
      return state.map((filter) => ({
        ...filter,
        isApplied: filter.name === action.payload,
      }))
    })
    builder.addCase(unapplySavedFilters.fulfilled, (state, action) => {
      return state.map((filter) => ({
        ...filter,
        isApplied: false,
      }))
    })
    builder.addCase(disableSavedFilters.fulfilled, (state) => {
      return state.map((filter) => ({
        ...filter,
        isDisabled: false,
      }))
    })
    builder.addCase(removeSavedFilter.fulfilled, (state, action) => {
      return state.filter((filter) => filter.name !== action.payload.name)
    })
    builder.addCase(getPreferenceFilters.fulfilled, (state, action) => {
      return action.payload
    })
  },
})

export const { saveImportedFilters } = savedFiltersSlice.actions

export default savedFiltersSlice.reducer
